import { Fragment } from 'react';
import { useSurveyI18n } from '../i18n';
import type { SurveyWeek } from '../report';
import { LIVE_COLOR, PLAYS_COLORS } from '../sections';
import type { Figures, Season } from '../seasons';
import { fmtDayRange, fmtDec, fmtDuration, fmtHours, fmtInt, fmtPct, ratio, share } from '../utils';
import { ColumnChart } from './ColumnChart';
import { SeasonHead } from './Overview';

/*
 * The per-week cards. Weeks are numbered inside their season ("Week 1" under
 * "Season 2"); where a week is named on its own — a tooltip, a table cell —
 * the season goes with it ("S2 · Week 1").
 */

type Props = { figures: Figures; seasons: Season[] };

const planned = (w: SurveyWeek) => w.status === 'planned';
const swatch = (s: Season) => <span className="su-dot" style={{ background: s.color }} />;

/** Players who completed at least one quiz, per week, grouped by season. */
export function PlayersPerWeek({ figures, seasons }: Props) {
  const { t } = useSurveyI18n();
  const named = (s: Season, k: number, w: SurveyWeek) => `${t.seasonShort(s.n)} · ${t.week(k + 1)}${w.status === 'live' ? t.inProgress : ''}`;
  return (
    <section className="su-card">
      <h2>{t.playersPerWeek}</h2>
      <div className="su-scroll">
        <div className="su-wide-chart">
          <ColumnChart
            height={260}
            groups={seasons.map((s) => ({
              key: s.id,
              caption: (
                <>
                  {swatch(s)}
                  {t.seasonPlayers(s.n, s.status === 'planned' ? '—' : fmtInt(s.figures.uniquePlayers))}
                </>
              ),
              columns: s.weeks.map((w, k) => ({
                key: w.week,
                label: t.week(k + 1),
                value: w.uniquePlayers,
                display: fmtInt(w.uniquePlayers),
                color: w.status === 'live' ? LIVE_COLOR : '#0F75B8',
                planned: planned(w),
                tip: planned(w) ? t.notStartedTip(named(s, k, w)) : t.playersTip(named(s, k, w), fmtInt(w.uniquePlayers)),
              })),
            }))}
          />
        </div>
      </div>
      <p className="su-note">
        {seasons.length === 1 ? t.playersNoteSeason(fmtInt(figures.uniquePlayers), seasons[0].n) : t.playersNote(fmtInt(figures.uniquePlayers))}
      </p>
    </section>
  );
}

/**
 * Share of each week's unique players by quizzes played that week (1, 2 … up to
 * the weekly maximum), with plays per user on the right — one column per season.
 */
export function PlaysDistribution({ seasons, weeklyPlayLimit }: { seasons: Season[]; weeklyPlayLimit: number }) {
  const { t } = useSurveyI18n();
  const buckets = Array.from({ length: weeklyPlayLimit }, (_v, i) => ({
    label: `${t.plays(i + 1)}${i + 1 === weeklyPlayLimit ? t.weeklyMaximum : ''}`,
    color: PLAYS_COLORS[i % PLAYS_COLORS.length],
    light: i >= 2,
  }));
  return (
    <section id="weekly" className="su-card">
      <h2>{t.playsDistribution}</h2>
      <div className="su-legend">
        {buckets.map((b) => (
          <span key={b.label}>
            <span className="su-swatch" style={{ background: b.color }} />
            {b.label}
          </span>
        ))}
      </div>
      <div className="su-col2">
        {seasons.map((s) => (
          <div key={s.id}>
            <SeasonHead season={s} detail={fmtDayRange(s.start, s.end, t.locale)} />
            <div className="su-dist">
              {s.weeks.map((w, k) => {
                const week = `${t.seasonShort(s.n)} · ${t.week(k + 1)}`;
                return (
                  <div className="su-dist-row" key={w.week}>
                    <span className={w.status === 'live' ? 'su-live-ink' : planned(w) ? 'su-muted' : ''}>{t.week(k + 1)}</span>
                    <div className="su-split short">
                      {planned(w) && <div className="empty">{t.notStartedYet}</div>}
                      {w.playsDistributionPct.map((pct, i) =>
                        pct > 0 ? (
                          <div
                            key={buckets[i]?.label ?? i}
                            className={buckets[i]?.light ? 'light' : ''}
                            style={{ width: `${pct}%`, background: buckets[i]?.color ?? PLAYS_COLORS[0] }}
                            data-tip={t.playedTimesTip(week, t.pct(Math.round(pct)), i + 1)}
                            aria-label={`${t.plays(i + 1)}: ${t.pct(Math.round(pct))}`}
                          >
                            {pct >= 8 ? t.pct(Math.round(pct)) : ''}
                          </div>
                        ) : null,
                      )}
                    </div>
                    <span>{planned(w) ? '—' : t.playsPerUser(fmtDec(ratio(w.plays, w.uniquePlayers)))}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="su-note">{t.distributionNote(weeklyPlayLimit)}</p>
    </section>
  );
}

/** Hours in the app per week, grouped by season, with the scope's and each season's figures beside the chart. */
export function TimePerWeek({ figures, seasons }: Props) {
  const { t } = useSurveyI18n();
  const measured = figures.timeInAppSec != null;
  const perUser = (f: Figures) => fmtDuration(ratio(f.timeInAppSec, f.uniquePlayers));
  const dash = (s: Season, v: string) => (s.status === 'planned' ? '—' : v);
  const stats = [
    { label: t.totalTimeInApp, value: fmtHours(figures.timeInAppSec) },
    { label: seasons.length === 1 ? t.avgTimeSeason(seasons[0].n) : t.avgTimeCampaign, value: perUser(figures) },
    ...(seasons.length > 1
      ? seasons.flatMap((s) => [
          { label: t.seasonTime(s.n), value: dash(s, fmtHours(s.figures.timeInAppSec)) },
          { label: t.avgTimeSeason(s.n), value: dash(s, perUser(s.figures)) },
        ])
      : []),
  ];
  return (
    <section className="su-card su-time">
      <div className="su-time-chart">
        <h2>{t.timePerWeek}</h2>
        <div className="su-scroll">
          <div className="su-wide-chart narrow">
            <ColumnChart
              height={240}
              unit=" h"
              ticks={2}
              gap={10}
              groups={seasons.map((s) => ({
                key: s.id,
                caption: (
                  <>
                    {swatch(s)}
                    {t.season(s.n)}
                  </>
                ),
                columns: s.weeks.map((w, k) => {
                  const week = `${t.seasonShort(s.n)} · ${t.week(k + 1)}${w.status === 'live' ? t.inProgress : ''}`;
                  return {
                    key: w.week,
                    // Ten columns beside the figures: "Wk 1", the full name is in the tooltip.
                    label: t.weekShort(k + 1),
                    sub: planned(w) ? '—' : fmtDuration(ratio(w.timeInAppSec, w.uniquePlayers)),
                    value: w.timeInAppSec == null ? null : w.timeInAppSec / 3600,
                    display: fmtHours(w.timeInAppSec),
                    color: w.status === 'live' ? LIVE_COLOR : '#3BB8C9',
                    planned: planned(w),
                    tip:
                      w.timeInAppSec == null
                        ? t.noTimeTip(week)
                        : t.timeTip(week, fmtHours(w.timeInAppSec), fmtDuration(ratio(w.timeInAppSec, w.uniquePlayers))),
                  };
                }),
              }))}
            />
          </div>
        </div>
        {!measured && <p className="su-note">{t.timeUnavailable}</p>}
      </div>
      <div className="su-time-list">
        {stats.map((s) => (
          <div key={s.label}>
            <span>{s.label}</span>
            <strong>{s.value}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Weekly summary: each season's weeks under its heading, its total, then the campaign total. */
export function WeeklyTable({ figures, seasons }: Props) {
  const { t } = useSurveyI18n();
  const cells = (f: Pick<Figures, 'uniquePlayers' | 'quizzesStarted' | 'plays' | 'ticketsIssued' | 'timeInAppSec'>) => [
    fmtInt(f.uniquePlayers),
    fmtInt(f.quizzesStarted),
    fmtInt(f.plays),
    fmtPct(share(f.plays, f.quizzesStarted)),
    fmtDec(ratio(f.plays, f.uniquePlayers)),
    fmtInt(f.ticketsIssued),
    fmtHours(f.timeInAppSec),
    fmtDuration(ratio(f.timeInAppSec, f.uniquePlayers)),
  ];
  const dashes = cells({ uniquePlayers: null, quizzesStarted: null, plays: null, ticketsIssued: null, timeInAppSec: null });
  return (
    <section className="su-card">
      <h2>{t.weeklySummary}</h2>
      <div className="su-scroll">
        <table className="su-table" aria-label={t.weeklySummary}>
          <thead>
            <tr>
              <th>{t.col.week}</th>
              <th>{t.col.uniquePlayers}</th>
              <th>{t.col.started}</th>
              <th>{t.col.plays}</th>
              <th>{t.col.completion}</th>
              <th>{t.col.playsPerUser}</th>
              <th>{t.col.tickets}</th>
              <th>{t.col.timeInApp}</th>
              <th>{t.col.timePerUser}</th>
            </tr>
          </thead>
          <tbody>
            {seasons.map((s) => (
              <Fragment key={s.id}>
                <tr className="season">
                  <td colSpan={9}>
                    {swatch(s)}
                    {t.season(s.n)} · {fmtDayRange(s.start, s.end, t.locale)}
                  </td>
                </tr>
                {s.weeks.map((w, k) => (
                  <tr key={w.week} className={planned(w) ? 'planned' : ''}>
                    <td className={w.status === 'live' ? 'su-live-ink' : ''}>
                      {t.week(k + 1)}
                      {w.status === 'live' ? t.inProgress : ''}
                    </td>
                    {cells(w).map((v, i) => (
                      <td key={i}>{v}</td>
                    ))}
                  </tr>
                ))}
                <tr className="subtotal">
                  <td>{t.seasonTotal(s.n)}</td>
                  {(s.status === 'planned' ? dashes : cells(s.figures)).map((v, i) => (
                    <td key={i}>{v}</td>
                  ))}
                </tr>
              </Fragment>
            ))}
            {seasons.length > 1 && (
              <tr className="total">
                <td>{t.campaignTotal}</td>
                {cells(figures).map((v, i) => (
                  <td key={i}>{v}</td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="su-note small">{t.weeklySumNote}</p>
    </section>
  );
}
